import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import CustomDropdown from "../../components/ui/CustomDropdown";

const OptimizeContentFilters = ({
  brandFilter,
  setBrandFilter,
  funnelStage,
  setFunnelStage,
  queryTypeFilter,
  setQueryTypeFilter,
  topicFilter,
  setTopicFilter,
  topicOptions = [],
  citationWindow,
  saveCitationWindow,
  periodOptions = [],
  isLoadingPeriod = false,
  queries = [],
}) => {
  const { t } = useTranslation();

  const brandOptions = [
    { value: "branded", label: t("common.yes", "Yes") },
    { value: "unbranded", label: t("common.no", "No") },
    { value: "all", label: t("common.all", "All") },
  ];

  const funnelOptions = [
    { value: "top", label: t("tracking.topFunnel", "Top") },
    { value: "middle", label: t("tracking.middleFunnel", "Middle") },
    { value: "bottom", label: t("tracking.bottomFunnel", "Bottom") },
    { value: "all", label: t("common.all", "All") },
  ];

  const hasReal = queries.some(
    (q) => q.query_type === "real" || q.query_type === "ai_summary",
  );
  const hasSynthetic = queries.some((q) => q.query_type === "synthetic");
  const hasAdhoc = queries.some((q) => q.query_type === "adhoc");

  const queryTypeOptions = [
    {
      value: "real",
      label: t("promptLibrary.real", "Real"),
      disabled: !hasReal,
    },
    {
      value: "synthetic",
      label: t("promptLibrary.synthetic", "Synthetic"),
      disabled: !hasSynthetic,
    },
    {
      value: "adhoc",
      label: t("promptLibrary.adhoc", "Ad hoc"),
      disabled: !hasAdhoc,
    },
    { value: "all", label: t("common.all", "All") },
  ];

  const localizedPeriodOptions = useMemo(() => {
    const labelMap = {
      7: t("common.lastWeek", "Last Week"),
      14: t("common.last2Weeks", "Last 2 Weeks"),
      30: t("common.lastMonth", "Last Month"),
      90: t("common.last3Months", "Last 3 Months"),
      180: t("common.last6Months", "Last 6 Months"),
      365: t("common.lastYear", "Last Year"),
    };
    return periodOptions.map((opt) => ({
      ...opt,
      label: labelMap[opt.value] || opt.label,
    }));
  }, [periodOptions, t]);

  const localizedTopicOptions = useMemo(() => {
    return topicOptions.map((opt) =>
      opt.value === "all" ? { ...opt, label: t("common.all", "All") } : opt,
    );
  }, [topicOptions, t]);

  const cellStyle = {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    minWidth: 0,
  };
  const cellLabelStyle = {
    fontSize: "0.8rem",
    fontWeight: 500,
    color: "var(--text-secondary)",
    whiteSpace: "nowrap",
  };
  const dropdownStyle = { width: "100%", minWidth: 0 };
  const triggerStyle = { width: "100%", minWidth: 0, boxSizing: "border-box" };

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
        gap: "16px",
        width: "100%",
      }}
    >
      <div style={cellStyle}>
        <span style={cellLabelStyle}>
          {t("contentOptimizer.brand", "Is branded")}
        </span>
        <CustomDropdown
          options={brandOptions}
          value={brandFilter}
          onChange={setBrandFilter}
          ariaLabel={t("contentOptimizer.brand", "Is branded")}
          dataTestId="brand-filter"
          style={dropdownStyle}
          triggerStyle={triggerStyle}
        />
      </div>
      <div style={cellStyle}>
        <span style={cellLabelStyle}>
          {t("contentOptimizer.funnelStage", "Funnel Stage")}
        </span>
        <CustomDropdown
          options={funnelOptions}
          value={funnelStage}
          onChange={setFunnelStage}
          ariaLabel={t("contentOptimizer.funnelStage", "Funnel Stage")}
          dataTestId="funnel-stage-filter"
          style={dropdownStyle}
          triggerStyle={triggerStyle}
        />
      </div>
      <div style={cellStyle}>
        <span style={cellLabelStyle}>
          {t("contentOptimizer.queryType", "Query Type")}
        </span>
        <CustomDropdown
          options={queryTypeOptions}
          value={queryTypeFilter}
          onChange={setQueryTypeFilter}
          ariaLabel={t("contentOptimizer.queryType", "Query Type")}
          dataTestId="query-type-filter"
          style={dropdownStyle}
          triggerStyle={triggerStyle}
        />
      </div>
      <div style={cellStyle}>
        <span style={cellLabelStyle}>
          {t("contentOptimizer.topic", "Topic")}
        </span>
        <CustomDropdown
          options={localizedTopicOptions}
          value={topicFilter}
          onChange={setTopicFilter}
          ariaLabel={t("contentOptimizer.topic", "Topic")}
          dataTestId="topic-filter"
          style={dropdownStyle}
          triggerStyle={triggerStyle}
        />
      </div>
      <div style={cellStyle}>
        <span style={cellLabelStyle}>
          {t("contentOptimizer.period", "Period")}
        </span>
        <CustomDropdown
          options={localizedPeriodOptions}
          defaultValue={citationWindow}
          onChange={saveCitationWindow}
          disabled={isLoadingPeriod}
          ariaLabel={t("contentOptimizer.period", "Period")}
          dataTestId="citation-window-dropdown"
          style={dropdownStyle}
          triggerStyle={triggerStyle}
        />
      </div>
    </div>
  );
};

export default OptimizeContentFilters;
